import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeSearchResults } from "../components/utils/normalizeSearchResults";
import { buildSearchQueryString } from "../components/utils/buildSearchQueryString";
import {
  DEFAULT_PER_PAGE,
  createInitialPagination,
} from "../components/utils/pagination";

const USER_FACING_SEARCH_ERROR_MESSAGE =
  "検索に失敗しました。時間をおいて再度お試しください。";

export const useSearchableIndex = ({
  searchEndpoint,
  fetchIndex,
  createInitialSearchFilters,
  getResponseItems,
}) => {
  const [searchFilters, setSearchFilters] = useState(
    createInitialSearchFilters(),
  );
  const [hasExecutedSearch, setHasExecutedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // 検索結果用のページング情報
  const [searchPagination, setSearchPagination] = useState(
    createInitialPagination(DEFAULT_PER_PAGE),
  );

  // 直前リクエストを中断するためのAbortController
  const activeAbortControllerRef = useRef(null);

  // 「最新の検索だけ反映」するためのシーケンス番号
  const latestSearchSequenceRef = useRef(0);

  // unmount後のsetStateを避けたい場合（任意）
  const isComponentMountedRef = useRef(true);
  useEffect(() => {
    isComponentMountedRef.current = true;
    return () => {
      isComponentMountedRef.current = false;
      // unmount時も通信を止める
      if (activeAbortControllerRef.current) {
        activeAbortControllerRef.current.abort();
        activeAbortControllerRef.current = null;
      }
    };
  }, []);

  const hasSearchFilters = useMemo(() => {
    const hasKeyword =
      (searchFilters.keyword?.length ?? 0) > 0 ||
      String(searchFilters.keywordInputText ?? "").trim().length > 0;
  
    const hasRoleIds = (searchFilters.roleIds ?? []).length > 0;
    const hasPrefectureIds = (searchFilters.prefectureIds ?? []).length > 0;
  
    const priceValue = String(searchFilters.priceFilter?.value ?? "").trim();
    const hasPriceValue = priceValue.length > 0;
  
    return hasKeyword || hasRoleIds || hasPrefectureIds || hasPriceValue;
  }, [searchFilters]);

  // 通常一覧：検索中は自動更新しない
  useEffect(() => {
    if (hasSearchFilters) return;
    fetchIndex();
  }, [fetchIndex, hasSearchFilters]);

  const executeSearch = useCallback(
    async (filterOverride, options = {}) => {
      setHasExecutedSearch(true);

      const filtersToUse =
        filterOverride && typeof filterOverride === "object"
          ? { ...searchFilters, ...filterOverride }
          : searchFilters;

      // 検索時のページ番号と件数
      const page = options.page ?? 1;
      const per = options.per ?? DEFAULT_PER_PAGE;

      // 新しい検索の開始：前の検索を中断
      if (activeAbortControllerRef.current) {
        activeAbortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      activeAbortControllerRef.current = abortController;

      // この検索に割り当てるシーケンス番号（最新判定用）
      latestSearchSequenceRef.current += 1;
      const currentSearchSequence = latestSearchSequenceRef.current;

      setIsSearching(true);
      setSearchError(null);

      try {
        const queryString = buildSearchQueryString(filtersToUse);
        const params = new URLSearchParams(queryString);

        // ページング情報をクエリに付与
        params.set("page", String(page));
        params.set("per", String(per));

        const requestUrl = `${searchEndpoint}?${params.toString()}`;

        const response = await fetch(requestUrl, {
          credentials: "same-origin",
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(USER_FACING_SEARCH_ERROR_MESSAGE);
        }

        const responseJson = await response.json();
        const rawItems = getResponseItems(responseJson);
        const normalizedItems = normalizeSearchResults(rawItems);

        // 最新の検索以外は反映しない
        const isLatestSearch =
          currentSearchSequence === latestSearchSequenceRef.current;
        const canSetState = isComponentMountedRef.current === true;

        if (isLatestSearch && canSetState) {
          setSearchResults(normalizedItems);

          // 検索結果のページング情報を保存
          setSearchPagination(
            responseJson.pagination ?? createInitialPagination(per),
          );
        }
      } catch (error) {
        // Abortはエラー扱いにしない
        const isAbortError = error?.name === "AbortError";

        const isLatestSearch =
          currentSearchSequence === latestSearchSequenceRef.current;
        const canSetState = isComponentMountedRef.current === true;

        if (!isAbortError && isLatestSearch && canSetState) {
          setSearchResults([]);
          setSearchPagination(createInitialPagination(per));
          setSearchError(new Error(USER_FACING_SEARCH_ERROR_MESSAGE));
        }
      } finally {
        const isLatestSearch =
          currentSearchSequence === latestSearchSequenceRef.current;
        const canSetState = isComponentMountedRef.current === true;

        // 最新の検索だけ「検索中」を解除（古いfinallyが先に走っても壊さない）
        if (isLatestSearch && canSetState) {
          setIsSearching(false);
        }

        // 自分がactiveなら片付ける（後続検索がもう始まってたら触らない）
        if (activeAbortControllerRef.current === abortController) {
          activeAbortControllerRef.current = null;
        }
      }
    },
    [searchEndpoint, searchFilters, getResponseItems],
  );

  const mutateSearchResults = useCallback((updater) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
      activeAbortControllerRef.current = null;
    }
  
    latestSearchSequenceRef.current += 1;
  
    if (!isComponentMountedRef.current) return;
  
    setSearchResults((previousSearchResults) => {
      if (typeof updater === "function") {
        return updater(previousSearchResults);
      }
  
      return updater;
    });
  
    setIsSearching(false);
  }, []);

  const clearSearch = useCallback(() => {
    // 実行中検索を止めて、以降の遅延レスポンスも無効化
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
      activeAbortControllerRef.current = null;
    }
    latestSearchSequenceRef.current += 1;

    setHasExecutedSearch(false);
    setSearchFilters(createInitialSearchFilters());
    setSearchResults([]);
    setSearchPagination(createInitialPagination(DEFAULT_PER_PAGE));
    setSearchError(null);
    setIsSearching(false);
  }, [createInitialSearchFilters]);

  const markSearchAsNotExecuted = useCallback(() => {
    setHasExecutedSearch(false);
  }, []);

  return {
    searchFilters,
    setSearchFilters,
    hasSearchFilters,
    hasExecutedSearch,
    markSearchAsNotExecuted,
    executeSearch,
    searchResults,
    mutateSearchResults,
    searchPagination,
    isSearching,
    clearSearch,
  };
};
